import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { LeadSection } from "@/components/LeadSection";
import { StickyCta } from "@/components/StickyCta";
import { cases, type CaseStudy } from "@/lib/cases-data";
import { CaseShot, CaseLightbox } from "@/components/CasePreview";

const SITE = "https://shopify-freelancer.net";
const TITLE = "Shopify Kundenprojekte: Shopaufbau, Entwicklung, Optimierung";
const DESCRIPTION =
  "Ausgewählte Shopify-Projekte: Shopaufbau und Migration, Theme-Entwicklung und Conversion-Arbeit, mit Ausgangslage, umgesetzten Hebeln und den vorhandenen Vorher/Nachher-Werten.";

/** Passender Rückweg pro Projekt. */
const backlink: Record<string, { to: string; label: string }> = {
  snydergolf: { to: "/shopify-shop-erstellen-lassen", label: "Shop erstellen lassen" },
  kremka: { to: "/shopify-conversion-optimierung", label: "Conversion Optimierung" },
  deinlunu: { to: "/shopify-conversion-optimierung", label: "Conversion Optimierung" },
  maskabel: { to: "/shopify-theme-anpassung", label: "Theme & Entwicklung" },
  "logo-matten": { to: "/shopify-theme-anpassung", label: "Theme & Entwicklung" },
  gordigear: { to: "/shopify-conversion-optimierung", label: "Conversion Optimierung" },
};

export const Route = createFileRoute("/cases/")({
  component: CasesIndex,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}/cases` },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      { rel: "canonical", href: `${SITE}/cases` },
      { rel: "alternate", hrefLang: "de", href: "https://shopify-freelancer.net/cases" },
      { rel: "alternate", hrefLang: "en", href: "https://shopify-freelancer.net/en/cases" },
      { rel: "alternate", hrefLang: "x-default", href: "https://shopify-freelancer.net/cases" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: TITLE,
          itemListElement: cases.map((c, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: `${c.shop}, ${c.industry}`,
            url: `${SITE}/cases#${c.slug}`,
          })),
        }),
      },
    ],
  }),
});

function CasesIndex() {
  const [zoom, setZoom] = useState<CaseStudy | null>(null);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main>
        <section className="veil border-b border-border/60 py-16 md:py-20">
          <div className="mx-auto max-w-6xl px-5">
            <p className="font-mono text-[11px] uppercase tracking-[0.34em] text-primary">
              Kundenprojekte
            </p>
            <h1 className="mt-5 max-w-3xl text-4xl font-bold leading-[1.08] md:text-5xl">
              Shops, die ich <span className="text-signal">aufgebaut, weiterentwickelt
              und optimiert</span> habe
            </h1>
            <p className="mt-5 max-w-2xl text-muted-foreground">
              Shopaufbau und Migration, Theme- und Liquid-Entwicklung, Conversion-Arbeit.
              Pro Projekt sehen Sie die Ausgangslage, was konkret umgesetzt wurde und,
              wo vorhanden, die Werte vor und nach der Umsetzung.
            </p>
            <p className="mt-4 max-w-2xl text-xs leading-relaxed text-muted-foreground">
              Zu den Zahlen: Es sind projektspezifische Vorher/Nachher-Beobachtungen aus
              den genannten Zeiträumen, kein isolierter A/B-Nachweis. Saison, Werbung und
              Sortiment wirken mit. Gleiche Werte in Ihrem Shop sind damit nicht zugesagt.
            </p>
          </div>
        </section>

        <section className="py-14">
          <div className="mx-auto grid max-w-6xl gap-10 px-5">
            {cases.map((c) => (
              <article
                key={c.slug}
                id={c.slug}
                className="scroll-mt-28 overflow-hidden rounded-lg border border-border bg-card md:grid md:grid-cols-2"
              >
                <div className="border-b border-border p-4 md:border-b-0 md:border-r md:p-6">
                  <CaseShot study={c} onClick={() => setZoom(c)} />
                  <p className="mt-3 text-center text-xs text-muted-foreground">
                    Zum Vergrößern auf das Projektbild tippen
                  </p>
                </div>
                <div className="p-6 md:p-8">
                  <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                    {c.industry}
                  </p>
                  <h2 className="mt-2 text-xl font-semibold md:text-2xl">{c.shop}</h2>
                  <a
                    href={c.url}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="mt-1 inline-flex items-center gap-1.5 text-sm font-semibold text-primary"
                  >
                    {c.domain} <ArrowUpRight className="h-4 w-4" />
                  </a>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                    {c.summary}
                  </p>

                  {c.metrics.length > 0 && (
                    <div className="mt-5 grid grid-cols-2 gap-3">
                      {c.metrics.map((m) => (
                        <div key={m.label} className="contents">
                          <div className="rounded-md border border-border bg-background px-4 py-3">
                            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                              Vorher{m.beforeLabel ? ` · ${m.beforeLabel}` : ""}
                            </p>
                            <p className="mt-1 text-2xl font-bold tabular-nums">{m.before}</p>
                            <p className="text-xs text-muted-foreground">{m.label}</p>
                          </div>
                          <div className="rounded-md border border-signal/40 bg-signal/10 px-4 py-3">
                            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                              Nachher{m.afterLabel ? ` · ${m.afterLabel}` : ""}
                            </p>
                            <p className="text-signal mt-1 text-2xl font-bold tabular-nums">
                              {m.after}
                            </p>
                            <p className="text-xs text-muted-foreground">{m.label}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <h3 className="mt-6 text-sm font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                    Ausgangslage
                  </h3>
                  {c.situation.map((s) => (
                    <p key={s} className="mt-2 text-sm leading-relaxed text-muted-foreground">
                      {s}
                    </p>
                  ))}

                  <h3 className="mt-5 text-sm font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                    Umgesetzte Arbeit
                  </h3>
                  <ul className="mt-2 grid list-disc gap-1.5 pl-4 text-sm text-muted-foreground">
                    {c.work.map((w) => (
                      <li key={w}>{w}</li>
                    ))}
                  </ul>

                  <div className="mt-6 flex flex-wrap items-center gap-4 border-t border-border/60 pt-5 text-sm font-semibold">
                    <Link
                      to={backlink[c.slug]!.to}
                      className="inline-flex items-center gap-1.5 text-primary"
                    >
                      {backlink[c.slug]!.label} <ArrowUpRight className="h-4 w-4" />
                    </Link>
                    <Link to="/kontakt" className="text-muted-foreground hover:text-foreground">
                      Kostenlose Einschätzung erhalten
                    </Link>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>

        <LeadSection source="cases-uebersicht" />
      </main>
      <CaseLightbox study={zoom} onClose={() => setZoom(null)} />
      <StickyCta />
      <SiteFooter />
    </div>
  );
}
